import { classMap } from 'lit/directives/class-map.js';
import { ApplicationElement, html } from '../application_element/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import '../label/element.js';

/**
 * Price is formatted depending on current user locale settings.
 *
 * @prop price - The cost of item.
 * @prop currency - Money (price) currency.
 * @prop available - Boolean value presenting if item is sold/given or not.
 * @prop type - The type of price (options: negotiable, fixed, free).
 *
 * @cssprop [--free-label-bg: #ffa000] - Background of a free, available label.
 * @cssprop [--negotiable-label-bg: #009688] - Background of a negotiable, available label.
 */

export default class MarketplacePrice extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    price: { type: Number },
    type: { type: String },
    currency: { type: String },
    available: { type: Boolean },
  };

  /** @internal */
  get theme() {
    return this.available ? 'primary' : 'muted';
  }

  /** @internal */
  get free() {
    return this.type === 'free';
  }

  /** @internal */
  get formattedPrice() {
    return this.i18n.numberToCurrency(this.price, {
      unit: this.currency.toUpperCase(),
    });
  }

  /** @internal */
  get labelContent() {
    if (this.free) return this.t('free');
    if (this.type === 'negotiable') return this.t('negotiable');

    return this.formattedPrice;
  }

  /** @internal */
  renderNotAvailableLabel() {
    return html` <span> ${this.t(this.free ? 'given' : 'sold')} </span> `;
  }

  render() {
    return html`
      <ef-label
        layout="md"
        theme=${this.theme}
        class=${classMap({
          negotiable: this.type === 'negotiable' && this.available,
          free: this.free && this.available,
        })}>
        <span>${this.labelContent}</span>
        ${!this.available ? this.renderNotAvailableLabel() : null}
      </ef-label>
    `;
  }
}

MarketplacePrice.registerTagName('ef-marketplace-price');

customElements.get('ef-marketplace-price') ||
  customElements.define('ef-marketplace-price', MarketplacePrice);
